import React, { useEffect, useRef } from 'react';
import { createChart } from 'lightweight-charts';

const OscillatorChart = ({ 
  data, 
  series = [], // Array of {key: 'rsi', color: '#f59e0b', lineWidth: 2, name: 'RSI'}
  horizontalLines = [], // Array of {value: 70, color: '#ef4444', style: 1, width: 1}
  height = 250,
  darkMode = true,
  yAxisDomain = null, // [min, max] or null for auto
  title = ''
}) => {
  const chartContainerRef = useRef();
  const chartRef = useRef();

  useEffect(() => {
    if (!chartContainerRef.current || !data || data.length === 0) {
      console.log('OscillatorChart: Skipping render - missing data or container', {
        hasContainer: !!chartContainerRef.current,
        dataLength: data?.length || 0,
        title
      });
      return;
    }

    // Clean up previous chart
    if (chartRef.current) {
      try {
        chartRef.current.remove();
      } catch (error) {
        console.log('OscillatorChart: Error removing chart:', error);
      }
      chartRef.current = null;
    }

    try {
      // Helper function to parse timestamps
      const parseTimestamp = (dateStr) => {
        const dateObj = new Date(dateStr);
        return dateObj.getTime() / 1000;
      };

      // Helper function to clean data
      const cleanTimeSeriesData = (dataArray) => {
        return dataArray
          .sort((a, b) => a.time - b.time)
          .filter((d, index, arr) => index === 0 || d.time !== arr[index - 1].time);
      };
      
      console.log(`OscillatorChart (${title}): Processing data`, {
        totalDataPoints: data.length,
        seriesKeys: series.map(s => s.key),
        sampleData: data.slice(0, 2)
      });

      const chart = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: height,
        layout: {
          backgroundColor: darkMode ? '#1f2937' : '#ffffff',
          textColor: darkMode ? '#d1d5db' : '#333333',
          fontSize: 11,
        },
        grid: {
          vertLines: { 
            color: darkMode ? '#374151' : '#e5e7eb',
            visible: true,
          },
          horzLines: { 
            color: darkMode ? '#374151' : '#e5e7eb',
            visible: true,
          },
        },
        crosshair: {
          mode: 1,
        },
        rightPriceScale: {
          borderColor: darkMode ? '#4b5563' : '#d1d5db',
          borderVisible: true,
          autoScale: yAxisDomain ? false : true,
        },
        timeScale: {
          borderColor: darkMode ? '#4b5563' : '#d1d5db',
          borderVisible: true,
          timeVisible: true,
          secondsVisible: false,
        },
        localization: {
          locale: 'en-US',
          dateFormat: 'MMM dd, yyyy',
        },
      });

      chartRef.current = chart;

      // Add data series
      series.forEach(seriesConfig => {
        const lineSeries = chart.addLineSeries({
          color: seriesConfig.color,
          lineWidth: seriesConfig.lineWidth || 2,
          title: seriesConfig.name,
          priceLineVisible: false,
          // If yAxisDomain is specified, use autoscaleInfoProvider to enforce it
          ...(yAxisDomain && {
            autoscaleInfoProvider: () => ({
              priceRange: {
                minValue: yAxisDomain[0],
                maxValue: yAxisDomain[1],
              },
            }),
          }),
        });

        const rawData = data
          .filter(d => d[seriesConfig.key] !== null && d[seriesConfig.key] !== undefined)
          .map(d => ({
            time: parseTimestamp(d.date),
            value: d[seriesConfig.key],
          }));

        const cleanedData = cleanTimeSeriesData(rawData);
        
        console.log(`OscillatorChart (${title}): Series ${seriesConfig.key}`, {
          rawDataPoints: rawData.length,
          cleanedDataPoints: cleanedData.length,
          sampleValues: cleanedData.slice(0, 3).map(d => d.value)
        });
        
        lineSeries.setData(cleanedData);
      });

      // Add horizontal reference lines
      horizontalLines.forEach(line => {
        const referenceSeries = chart.addLineSeries({
          color: line.color,
          lineWidth: line.width || 1,
          lineStyle: line.style || 2, // 2 = dashed
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
        });

        // Create a constant line across the entire time range
        if (data.length > 0) {
          const constantLineData = [
            { time: parseTimestamp(data[0].date), value: line.value },
            { time: parseTimestamp(data[data.length - 1].date), value: line.value }
          ];
          referenceSeries.setData(constantLineData);
        }
      });

      // Fit content
      chart.timeScale().fitContent();

      // Handle resize
      const handleResize = () => {
        if (chartContainerRef.current && chartRef.current) {
          chartRef.current.applyOptions({
            width: chartContainerRef.current.clientWidth,
          });
        }
      };

      window.addEventListener('resize', handleResize);

      // Cleanup
      return () => {
        window.removeEventListener('resize', handleResize);
        if (chartRef.current) {
          chartRef.current.remove();
          chartRef.current = null;
        }
      };
    } catch (error) {
      console.error('OscillatorChart: Error creating chart:', error);
      return () => {};
    }
  }, [data, series, horizontalLines, height, darkMode, yAxisDomain]);

  return (
    <div 
      ref={chartContainerRef} 
      key={`oscillator-${darkMode ? 'dark' : 'light'}-${title}`}
      style={{ width: '100%', height: `${height}px` }} 
    />
  );
};

export default OscillatorChart;
